import { Box, Stack, Typography } from "@mui/material";
import { RequirementCitations } from "components/Submission/RequirementCitations";
import { getWasteSubmissionFormName } from "encamp-shared/src/submissions/wasteSubmissionForms";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { SubmissionStepType } from "generated-graphql/graphql";
import { useAcknowledgeUnderstandStep } from "hooks/useAcknowledgeUnderstandStep";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback } from "react";
import { V2DisplayOnlyField as DisplayOnlyField } from "routes/Customer/Facility/ProfileV2/V2DisplayOnlyField";
import { formatDateLikeForDisplay } from "util/dates";

import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";
import { StepContinueFooter } from "./StepContinueFooter";

/**
 * Understand step (document forms): what this submission is, why it is
 * required, and its scope. Rendered entirely from the shell query — the step
 * fetches no data of its own. First step of the flow, so no Back.
 */
export function UnderstandStep({ submissionId }: { submissionId: string; facilityId: string }) {
  const { submission, canEdit } = useWasteStepper();
  const { user } = useCurrentUser();
  const { acknowledgeStep, loading: acknowledging } = useAcknowledgeUnderstandStep(submissionId);

  // Reviewers/signers (non-editing customers) acknowledge this step once on their
  // own row; staff/editors record shared step progress through the default footer.
  const myReviewer = (submission?.reviewers ?? []).find((r) => r.user.id === user?.id);
  const isReviewerView = !canEdit && !!myReviewer;
  const hasAcknowledged = !!myReviewer?.acknowledgedAt;

  const handleReviewerContinue = useCallback(async () => {
    if (!hasAcknowledged) await acknowledgeStep();
  }, [hasAcknowledged, acknowledgeStep]);

  // Reviewers continue straight to where they act, skipping the read-only steps.
  const steps = (submission?.stepperSteps ?? []).map(({ step }) => step);
  const reviewerNextStep = steps.includes(SubmissionStepType.ReviewAndSign)
    ? SubmissionStepType.ReviewAndSign
    : undefined;

  // Named forms (8700-13) show their EPA form name; one-off combos fall back
  // to the submission type.
  const formName =
    getWasteSubmissionFormName(submission?.submissionType, submission?.submissionMethod) ??
    (submission?.submissionType ? prettyPrintEnumValue(submission.submissionType) : "");
  const notes = submission?.notes ?? "";
  const citations = submission?.requirementCitations ?? [];
  const facility = submission?.facilitySubmissions?.[0]?.facility;
  const facilityDisplay = [facility?.name, facility?.state].filter(Boolean).join(", ");

  return (
    <Stack spacing={3}>
      <StepHeader
        title="Understand"
        description="What this submission is, why it is required, and what it covers."
      />
      <DisplayOnlyField label="Form" text={formName} />
      {citations.length > 0 && (
        <Stack spacing={1}>
          <StepSubHeader title="Why this is required" />
          <RequirementCitations citations={citations} />
        </Stack>
      )}
      <Stack spacing={1}>
        <StepSubHeader title="Notes" />
        {notes ? (
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
            {notes}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No notes have been added.
          </Typography>
        )}
      </Stack>
      <Stack spacing={1}>
        <StepSubHeader title="Scope" />
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-start" }}>
          <Box sx={{ flex: 1, minWidth: 280 }}>
            <DisplayOnlyField label="Facility" text={facilityDisplay} />
          </Box>
          <Box sx={{ width: 220 }}>
            <DisplayOnlyField
              label="Due date"
              text={submission?.dueDate ? formatDateLikeForDisplay(submission.dueDate) : ""}
            />
          </Box>
          <Box sx={{ width: 220 }}>
            <DisplayOnlyField
              label="Submission method"
              text={
                submission?.submissionMethod
                  ? prettyPrintEnumValue(submission.submissionMethod)
                  : ""
              }
            />
          </Box>
        </Box>
      </Stack>
      {isReviewerView ? (
        <StepContinueFooter
          submissionId={submissionId}
          step={SubmissionStepType.Understand}
          label="Continue"
          canContinue
          onContinue={handleReviewerContinue}
          nextStep={reviewerNextStep}
          loading={acknowledging}
        />
      ) : (
        <StepContinueFooter
          submissionId={submissionId}
          step={SubmissionStepType.Understand}
          label="Continue"
        />
      )}
    </Stack>
  );
}
